import React, { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { BarChart } from "@mui/x-charts/BarChart";
import dayjs from "dayjs";
import { PAY_PLAN_APP_LABELS, PAY_PLAN_APPS_FOR_ADMIN, SIGNUP_SOURCE_LABELS } from "../../services/PayPlanUtil";
import Logger from "../../services/Logger";
import AccountService from "./AccountService";

const PERIOD_OPTIONS = [
    { value: "all", label: "All" },
    { value: "month", label: "This month" },
    { value: "week", label: "This week" },
];
const PERIOD_VALUES = new Set(PERIOD_OPTIONS.map((option) => option.value));
const PERIOD_STORAGE_KEY = "admin.account.stats.period";

const APP_SERIES_COLORS = {
    web: "#1976d2",
    tutor: "#9c27b0",
    sliding_puzzle_quest: "#ed6c02",
    jigsaw_muse: "#2e7d32",
    word_wheel_quest: "#c9a227",
};

const SOURCE_SERIES_COLORS = {
    WEB: "#1976d2",
    IOS: "#111111",
    ANDROID: "#3ddc84",
    APPLE: "#6d6d6d",
    GOOGLE: "#ea4335",
    UNKNOWN: "#9e9e9e",
};

const APP_SERIES = PAY_PLAN_APPS_FOR_ADMIN.map((code) => ({
    id: code,
    label: PAY_PLAN_APP_LABELS[code] ?? code,
    color: APP_SERIES_COLORS[code] ?? "#1976d2",
}));

const SOURCE_SERIES = Object.keys(SIGNUP_SOURCE_LABELS).map((source) => ({
    id: source,
    label: SIGNUP_SOURCE_LABELS[source] ?? source,
    color: SOURCE_SERIES_COLORS[source] ?? "#9e9e9e",
}));

function testAccountParam(filter) {
    if (filter === "exclude") return false;
    if (filter === "include") return true;
    return undefined;
}

function readStoredPeriod() {
    try {
        const stored = localStorage.getItem(PERIOD_STORAGE_KEY);
        return PERIOD_VALUES.has(stored) ? stored : "all";
    } catch {
        return "all";
    }
}

function periodCaption(period, createdFrom) {
    if (period === "week") {
        return createdFrom
            ? `Signed-up users per day since Monday ${new Date(createdFrom).toLocaleDateString()}.`
            : "Signed-up users per day this calendar week (Monday–Sunday).";
    }
    if (period === "month") {
        return createdFrom
            ? `Signed-up users per day since ${new Date(createdFrom).toLocaleDateString()}.`
            : "Signed-up users per day this calendar month.";
    }
    return "Signed-up users by month.";
}

function eachBucketInclusive(startIso, endIso, bucket) {
    const start = dayjs(startIso);
    const end = dayjs(endIso);
    if (!start.isValid() || !end.isValid()) {
        return [];
    }
    const points = [];
    if (bucket === "month") {
        let cursor = start.startOf("month");
        const last = end.startOf("month");
        while (cursor.isBefore(last) || cursor.isSame(last, "month")) {
            points.push(cursor);
            cursor = cursor.add(1, "month");
        }
        return points;
    }
    let cursor = start.startOf("day");
    const last = end.startOf("day");
    while (cursor.isBefore(last) || cursor.isSame(last, "day")) {
        points.push(cursor);
        cursor = cursor.add(1, "day");
    }
    return points;
}

function bucketKey(day, bucket) {
    return bucket === "month" ? day.format("YYYY-MM-01") : day.format("YYYY-MM-DD");
}

function tickFormatFor(period, bucket, count) {
    if (bucket === "month") {
        return "MMM YYYY";
    }
    if (period === "week") {
        return "ddd M/D";
    }
    return count > 16 ? "M/D" : "ddd M/D";
}

function DateSeriesBarChart({ title, period, bucket, rangeStart, rangeEnd, seriesDefs, rows, dateField, seriesField }) {
    const days = useMemo(
        () => eachBucketInclusive(rangeStart, rangeEnd, bucket),
        [rangeStart, rangeEnd, bucket],
    );
    const keys = useMemo(() => days.map((day) => bucketKey(day, bucket)), [days, bucket]);
    const labels = useMemo(() => {
        const format = tickFormatFor(period, bucket, days.length);
        return days.map((day) => day.format(format));
    }, [days, period, bucket]);
    const series = useMemo(() => {
        const byId = new Map(seriesDefs.map((def) => [def.id, keys.map(() => 0)]));
        (Array.isArray(rows) ? rows : []).forEach((row) => {
            const seriesId = row?.[seriesField];
            const values = byId.get(seriesId);
            if (!values) return;
            const idx = keys.indexOf(row?.[dateField]);
            if (idx < 0) return;
            values[idx] = Number(row.count) || 0;
        });
        return seriesDefs.map((def) => ({
            id: def.id,
            data: byId.get(def.id) ?? keys.map(() => 0),
            label: def.label,
            color: def.color,
            stack: "users",
        }));
    }, [seriesDefs, rows, keys, dateField, seriesField]);

    const angled = labels.length > 10;

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                    {title}
                </Typography>
                <BarChart
                    height={360}
                    grid={{ horizontal: true }}
                    xAxis={[
                        {
                            scaleType: "band",
                            data: labels,
                            label: "Date",
                            tickLabelStyle: {
                                angle: angled ? -45 : 0,
                                textAnchor: angled ? "end" : "middle",
                                fontSize: 11,
                            },
                        },
                    ]}
                    yAxis={[{ label: "Users", min: 0 }]}
                    series={series}
                    slotProps={{ legend: { hidden: false } }}
                />
            </CardContent>
        </Card>
    );
}

export default function AccountAppStats({
    keyword = "",
    testAccountFilter = "exclude",
    excludeStaffUser = false,
    excludeAppleStaff = false,
}) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [period, setPeriod] = useState(readStoredPeriod);

    useEffect(() => {
        try {
            localStorage.setItem(PERIOD_STORAGE_KEY, period);
        } catch {
            /* ignore quota / private mode */
        }
    }, [period]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await AccountService.countsByApp({
                    keyword,
                    testAccount: testAccountParam(testAccountFilter),
                    staff: excludeStaffUser ? false : undefined,
                    excludeApplePrivateRelay: excludeAppleStaff,
                    period,
                });
                if (!cancelled) setStats(result ?? null);
            } catch (err) {
                Logger.error("Failed to load account app stats:", err);
                if (!cancelled) {
                    setError("Failed to load user counts.");
                    setStats(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [keyword, testAccountFilter, excludeStaffUser, excludeAppleStaff, period]);

    const sourceSeries = useMemo(() => {
        const rows = Array.isArray(stats?.signupSourcesByDate) ? stats.signupSourcesByDate : [];
        const totals = Array.isArray(stats?.signupSources) ? stats.signupSources : [];
        const used = new Set(
            [
                ...rows.filter((row) => Number(row.count) > 0).map((row) => row.source),
                ...totals.filter((row) => Number(row.count) > 0).map((row) => row.source),
            ].filter(Boolean),
        );
        const visible = SOURCE_SERIES.filter((def) => used.has(def.id));
        return visible.length > 0 ? visible : SOURCE_SERIES;
    }, [stats]);

    const bucket = stats?.bucket === "month" ? "month" : "day";
    const rangeStart = stats?.rangeStart;
    const rangeEnd = stats?.rangeEnd;

    const periodToolbar = (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
                mb: 2,
            }}
        >
            <Typography variant="body2" color="text.secondary" sx={{ flex: "1 1 240px" }}>
                {periodCaption(period, stats?.createdFrom)}
                {" "}A user can appear in more than one app.
            </Typography>
            <ToggleButtonGroup
                exclusive
                size="small"
                value={period}
                onChange={(event, value) => {
                    if (value) setPeriod(value);
                }}
                aria-label="User count period"
            >
                {PERIOD_OPTIONS.map((option) => (
                    <ToggleButton
                        key={option.value}
                        value={option.value}
                        sx={{ px: 1.5, minHeight: 44 }}
                    >
                        {option.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Box>
    );

    if (error) {
        return (
            <Box>
                {periodToolbar}
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box>
            {periodToolbar}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <DateSeriesBarChart
                        title="Users by app"
                        period={period}
                        bucket={bucket}
                        rangeStart={rangeStart}
                        rangeEnd={rangeEnd}
                        seriesDefs={APP_SERIES}
                        rows={stats?.appsByDate}
                        dateField="date"
                        seriesField="appCode"
                    />
                    <DateSeriesBarChart
                        title="Users by sign-up source"
                        period={period}
                        bucket={bucket}
                        rangeStart={rangeStart}
                        rangeEnd={rangeEnd}
                        seriesDefs={sourceSeries}
                        rows={stats?.signupSourcesByDate}
                        dateField="date"
                        seriesField="source"
                    />
                </>
            )}
        </Box>
    );
}
